import { useEffect, useState } from 'react';
import api from '../../api';

// Loads info + health for a set of gateway devices up front, so the page can render
// fully-populated cards in one pass instead of each card fetching on its own (which
// made the name/id appear first and the rest pop in afterwards).
const useHttpGetGatewayDetailsForDevices = devices => {
    const [gateways, setGateways] = useState([]);
    const [fetchedKey, setFetchedKey] = useState(null);
    const key = devices.map(d => d.id).join(',');

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const result = await Promise.all(
                    devices.map(async device => {
                        const [infoRes, healthRes, metaRes] = await Promise.all([
                            api.get_device_info(device.id).catch(() => null),
                            api.get_device_gateway_health(device.id).catch(() => null),
                            // Inverter/gateway hardware metadata lives in the dedicated endpoint
                            // (404 = none yet), the same source the single gateway page uses.
                            api.get_device_gateway_metadata(device.id).catch(() => null),
                        ]);
                        return {
                            ...device,
                            // Merge metadata over info so the card's hardware fields
                            // (inverter_manufacturer/model/serial/firmware, gateway_hardware_version)
                            // are populated even though get_device_info doesn't carry them.
                            info: { ...(infoRes?.data?.info ?? {}), ...(metaRes?.data ?? {}) },
                            health: healthRes?.data ?? null,
                        };
                    })
                );
                if (active) {
                    setGateways(result);
                }
            } finally {
                if (active) {
                    setFetchedKey(key);
                }
            }
        };

        load();
        return () => {
            active = false;
        };
    }, [key]);

    const isReady = fetchedKey === key;
    return { gateways, isReady };
};

export default useHttpGetGatewayDetailsForDevices;
